/* eslint-disable react-hooks/exhaustive-deps */
import { DownOutlined } from "@ant-design/icons";
import { Table, Button, Pagination, DatePicker, Tag, Select, Dropdown, Input, Modal, Grid, Row, Col } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState, useRef } from "react";
import { CSVLink } from "react-csv";

import ReplyUserQueryModal from "./ReplyUserQueryModal";
import { selectFormOptionsNoFormatting } from "../../utils/helper";
import { showErrorNotification, showSuccessNotification } from "../../utils/notification";

export const REQUEST_TYPES = {
  CONTACT_US: "CONTACT_US",
  CALLBACK_REQUEST: "CALLBACK_REQUEST",
};

export const RESOLUTION_MEDIUMS = {
  EMAIL: "EMAIL",
  WHATSAPP: "WHATSAPP",
  PHONE: "PHONE",
};

const UserQueriesDash = ({ ROOT_URL }) => {
  const screens = Grid.useBreakpoint();
  const isMobileView = screens.md === false;

  const currentDate = new Date();
  const oneMonthAgo = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 1,
    currentDate.getDate()
  );

  const [filter, setFilter] = useState({
    status: "OPEN",
    startDate: oneMonthAgo,
    endDate: currentDate,
    isPaidUser: undefined,
    requestType: undefined
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [queriesList, setQueriesList] = useState([]);
  const [totalQueries, setTotalQueries] = useState(0);
  const [isQueryResponseModal, setIsQueryResponseModal] = useState(false);

  const [currentQueryId, setCurrentQueryId] = useState();
  const [contactUsCsvData, setContactUsCsvData] = useState([]);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [resolveRequestId, setResolveRequestId] = useState(null);
  const [resolveMedium, setResolveMedium] = useState(RESOLUTION_MEDIUMS.PHONE);
  const [callSummaryLog, setCallSummaryLog] = useState("");

  const csvLinkRef = useRef(null);

  const fetchUserQueryRequests = () => {
    axios
      .get(`${ROOT_URL}/admin/user-queries/list`, {
        params: {
          page: currentPage,
          filter
        }
      })
      .then((res) => {
        const { queries, totalQueriesCount } = res.data;
        setQueriesList(queries);
        setTotalQueries(totalQueriesCount);
        showSuccessNotification("Requests Fetched Successfully");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleMarkQueryAsResolved = (requestId, resolutionMedium, callSummary = "") => {
    axios
      .post(`${ROOT_URL}/admin/user-queries/mark-resolved`, {
        requestId,
        resolvedBy: resolutionMedium,
        callSummary
      })
      .then(() => {
        showSuccessNotification(`Query Resolved by ${resolutionMedium}`);
        fetchUserQueryRequests();
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDownloadCsv = () => {
    axios
      .get(`${ROOT_URL}/admin/user-queries/fetch-download-data`, {
        params: {
          filter
        }
      })
      .then((res) => {
        const contactUsDataList = res?.data || [];
        setContactUsCsvData(
          contactUsDataList?.map((contactUsData) => {
            return {
              "Creation Date": dayjs(contactUsData?.creationDate)?.format("YYYY-MM-DD HH:mm:ss"),
              "User Name": contactUsData?.name,
              "User Email": contactUsData?.email,
              "User Phone": contactUsData?.phone,
              "Query Title": contactUsData?.queryTitle,
              "Query Description": contactUsData?.queryDescription,
              Status: contactUsData?.status,
              "Request Type": contactUsData?.requestType === REQUEST_TYPES.CALLBACK_REQUEST ? "CALLBACK" : "EMAIL",
              "Is Paid User": contactUsData?.isPaidUser ? "YES" : "NO",
              "Courses Purchased": contactUsData?.coursesPurchased?.join(", ") || "None"
            };
          })
        );
        showSuccessNotification("CSV downloaded successfully");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  useEffect(() => {
    if (contactUsCsvData.length > 0 && csvLinkRef.current) {
      csvLinkRef.current.link.click();
    }
  }, [contactUsCsvData]);

  const openResolveModal = (requestId, medium) => {
    setResolveRequestId(requestId);
    setResolveMedium(medium);
    setCallSummaryLog("");
    setIsResolveModalOpen(true);
  };

  const getRespondButtonLabel = (row) => {
    const isCallback = row.requestType === REQUEST_TYPES.CALLBACK_REQUEST;
    if (row.status === "RESOLVED") {
      return "Re-open";
    }
    return isCallback ? "Add Call Log" : "Respond";
  };

  const getMarkResolvedMenu = (requestId) => ({
    items: [
      {
        key: "email",
        label: "Mark Resolved by Email",
        onClick: () => openResolveModal(requestId, RESOLUTION_MEDIUMS.EMAIL)
      },
      {
        key: "whatsapp",
        label: "Mark Resolved by WhatsApp",
        onClick: () => openResolveModal(requestId, RESOLUTION_MEDIUMS.WHATSAPP)
      },
      {
        key: "phone",
        label: "Mark Resolved by Phone",
        onClick: () => openResolveModal(requestId, RESOLUTION_MEDIUMS.PHONE)
      }
    ]
  });

  const columns = [
    {
      title: "S No.",
      dataIndex: "index",
      key: "index",
      width: 70,
      fixed: isMobileView ? undefined : "left"
    },
    {
      title: "Creation Date",
      dataIndex: "creationDate",
      key: "creationDate",
      width: 100,
      render: (creationDate) => dayjs(creationDate).format("DD MMM YYYY | HH:mm")
    },
    {
      title: "Type",
      dataIndex: "requestType",
      key: "requestType",
      width: 110,
      render: (type) => {
        const isCallback = type === REQUEST_TYPES.CALLBACK_REQUEST;
        return (
          <Tag color={isCallback ? "purple" : "cyan"}>
            {isCallback ? "CALLBACK" : "EMAIL"}
          </Tag>
        );
      }
    },
    {
      title: "From Page",
      dataIndex: "from",
      key: "from",
      width: 100,
      render: (from) => {
        return <Tag color={"green"}>{from?.toUpperCase() || "--"}</Tag>;
      }
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 160,
      render: (text) => (
        <div
          style={{
            wordBreak: "break-word",
            whiteSpace: "pre-wrap"
          }}
        >
          {text}
        </div>
      )
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 100,
      render: (text) => (
        <div
          style={{
            wordBreak: "break-word",
            whiteSpace: "pre-wrap"
          }}
        >
          {text}
        </div>
      )
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 100,
      render: (text) => {
        if (!text) return <span>--</span>;
        return (
          <div
            style={{
              wordBreak: "break-word",
              whiteSpace: "pre-wrap"
            }}
          >
            {text}
          </div>
        );
      }
    },
    {
      title: "Query Title",
      dataIndex: "queryTitle",
      key: "queryTitle",
      width: 200,
      render: (text) => (
        <div
          style={{
            wordBreak: "break-word",
            whiteSpace: "pre-wrap"
          }}
        >
          {text}
        </div>
      )
    },
    {
      title: "Courses Purchased",
      dataIndex: "coursesPurchased",
      key: "coursesPurchased",
      width: 100,
      render: (courses) => {
        if (!courses || courses.length === 0) {
          return <span>None</span>;
        }
        return (
          <div>
            {courses.map((course, index) => (
              <Tag key={index} color="blue" style={{ marginBottom: 2, fontSize: "12px" }}>
                {course}
              </Tag>
            ))}
          </div>
        );
      }
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (row) => {
        const statusMap = {
          OPEN: { color: "blue", label: "Open" },
          IN_PROGRESS: { color: "orange", label: "In Progress" },
          RESOLVED: { color: "green", label: "Resolved" }
        };

        const statusObj = statusMap[row.status] || { color: "gray", label: "Unknown" };

        return (
          <Tag color={statusObj.color} key={statusObj.label}>
            {statusObj.label.toUpperCase()}
          </Tag>
        );
      }
    },
    {
      title: "Query Description",
      dataIndex: "queryDescription",
      key: "queryDescription",
      width: 320,
      render: (text) => (
        <div
          style={{
            wordBreak: "break-word",
            whiteSpace: "pre-wrap"
          }}
        >
          {text}
        </div>
      )
    },
    {
      title: "Actions",
      key: "action",
      width: 180,
      fixed: isMobileView ? undefined : "right",
      render: (row) => (
        <div
          id="user_requests_row_action"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px"
          }}
        >
          <Button
            type="primary"
            size="small"
            onClick={() => {
              setCurrentQueryId(row.key);
              setIsQueryResponseModal(true);
            }}
            style={{ fontSize: "11px", padding: "2px 8px" }}
          >
            {getRespondButtonLabel(row)}
          </Button>
          {row.status !== "RESOLVED" && (
            <Dropdown menu={getMarkResolvedMenu(row.key)} trigger={["click"]}>
              <Button type="primary" size="small" style={{ fontSize: "11px", padding: "2px 8px" }}>
                Mark Resolved <DownOutlined />
              </Button>
            </Dropdown>
          )}
        </div>
      )
    }
  ];

  useEffect(() => {
    fetchUserQueryRequests();
  }, [filter, currentPage]);

  return (
    <div
      style={{
        margin: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "15px",
        height: "calc(100vh - 100px)"
      }}
    >
      {/* Filters Section */}
      <Row gutter={[12, 12]} style={{ padding: "15px", borderRadius: "8px" }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "500" }}>Date Range:</span>
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              value={
                filter.startDate && filter.endDate
                  ? [dayjs(filter.startDate), dayjs(filter.endDate)]
                  : []
              }
              onChange={(dates) => {
                if (dates) {
                  setFilter((prevFilter) => ({
                    ...prevFilter,
                    startDate: dates[0].startOf("day").toDate(),
                    endDate: dates[1].endOf("day").toDate()
                  }));
                } else {
                  setFilter((prevFilter) => ({
                    ...prevFilter,
                    startDate: null,
                    endDate: null
                  }));
                }
              }}
            />
          </div>
        </Col>

        <Col xs={24} sm={12} md={8} lg={5}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "500" }}>Status:</span>
            <Select
              placeholder="Select Status"
              style={{ width: "100%" }}
              allowClear
              onClear={() => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  status: undefined
                }));
              }}
              value={filter.status}
              onChange={(value) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  status: value
                }));
              }}
            >
              {selectFormOptionsNoFormatting(["OPEN", "IN_PROGRESS", "RESOLVED"]).map((item) => (
                <Select.Option value={item.value} key={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Col>

        <Col xs={24} sm={12} md={8} lg={5}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "500" }}>Paid User:</span>
            <Select
              placeholder="Select Paid User"
              style={{ width: "100%" }}
              allowClear
              onClear={() => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  isPaidUser: undefined
                }));
              }}
              value={filter.isPaidUser}
              onChange={(value) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  isPaidUser: value
                }));
              }}
            >
              <Select.Option value={undefined}>All Users</Select.Option>
              <Select.Option value={true}>Paid Users Only</Select.Option>
              <Select.Option value={false}>Non-Paid Users Only</Select.Option>
            </Select>
          </div>
        </Col>

        <Col xs={24} sm={12} md={8} lg={5}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "500" }}>Request Type:</span>
            <Select
              placeholder="Select Request Type"
              style={{ width: "100%" }}
              allowClear
              onClear={() => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  requestType: undefined
                }));
              }}
              value={filter.requestType}
              onChange={(value) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  requestType: value
                }));
              }}
            >
              <Select.Option value={undefined}>All Types</Select.Option>
              <Select.Option value="CONTACT_US">Email Support</Select.Option>
              <Select.Option value="CALLBACK_REQUEST">Callbacks</Select.Option>
            </Select>
          </div>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "500" }}>Request ID:</span>
            {/* The backend ignores the other filters for an ID lookup, so the request shows whatever its date or status. */}
            <Input.Search
              placeholder="Search by request ID"
              allowClear
              onSearch={(value) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  requestId: value.trim() || undefined
                }));
                setCurrentPage(1);
              }}
            />
          </div>
        </Col>

        <Col xs={24} sm={12} md={8} lg={3}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", height: "100%" }}>
            <span style={{ fontWeight: "500", visibility: "hidden" }}>Export:</span>
            <CSVLink
              asyncOnClick={true}
              data={contactUsCsvData || []}
              ref={csvLinkRef}
              filename={"contactUsData.csv"}
            />
            <Button
              type="primary"
              style={{ width: "100%" }}
              onClick={() => {
                handleDownloadCsv();
              }}
            >
              Download CSV
            </Button>
          </div>
        </Col>
      </Row>

      <div style={{ flex: 1, overflow: "hidden" }}>
        <Table
          columns={columns}
          dataSource={queriesList}
          bordered
          pagination={false}
          scroll={{
            x: "max-content",
            y: "calc(100vh - 300px)"
          }}
          size="small"
          sticky={{
            offsetHeader: 0
          }}
          rowKey="key"
        />
      </div>

      <div style={{ display: "flex", justifyContent: "center", padding: "10px 0" }}>
        <Pagination
          total={totalQueries}
          showSizeChanger={false}
          showQuickJumper={false}
          pageSize={50}
          current={currentPage}
          onChange={handlePageChange}
        />
      </div>

      {isQueryResponseModal === true && (
        <ReplyUserQueryModal
          isModalOpen={isQueryResponseModal}
          setIsModalOpen={setIsQueryResponseModal}
          currentQueryId={currentQueryId}
          ROOT_URL={ROOT_URL}
        />
      )}

      {isResolveModalOpen && (
        <Modal
          title="Mark Query as Resolved"
          open={isResolveModalOpen}
          onOk={() => {
            handleMarkQueryAsResolved(resolveRequestId, resolveMedium, callSummaryLog);
            setIsResolveModalOpen(false);
          }}
          onCancel={() => setIsResolveModalOpen(false)}
          okText="Resolve"
          cancelText="Cancel"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "15px" }}>
            <div>
              <span style={{ fontWeight: "500", display: "block", marginBottom: "6px" }}>Resolution Medium:</span>
              <Select
                value={resolveMedium}
                onChange={(value) => setResolveMedium(value)}
                style={{ width: "100%" }}
              >
                <Select.Option value="EMAIL">Email</Select.Option>
                <Select.Option value="WHATSAPP">WhatsApp</Select.Option>
                <Select.Option value="PHONE">Phone Call</Select.Option>
              </Select>
            </div>
            <div>
              <span style={{ fontWeight: "500", display: "block", marginBottom: "6px" }}>Call / Resolution Summary Log (Optional):</span>
              <Input.TextArea
                rows={4}
                placeholder="Log notes about the resolution or conversation here..."
                value={callSummaryLog}
                onChange={(e) => setCallSummaryLog(e.target.value)}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default UserQueriesDash;
